"use client";

import { useState } from "react";
import { LuClipboardList, LuSend } from "react-icons/lu";

import SendFormsDialog from "@/components/forms/send-forms-dialog";
import { Alert, AlertDescription, Button, Checkbox } from "@/components/ui";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useInPersonForm } from "@/hooks/use-in-person-form";

import { formatDate } from "@/lib/format-data";
import { toast } from "@/lib/use-toast";

import type { UserTiny } from "@/types/user";

import { useRefillFormsMutation } from "@/api/forms/form-mutation";

export interface RefillFormTarget {
  form: { id: string; name: string; pathName: string };
  /** When the copy being replaced was submitted. */
  completedDate?: string;
}

interface RefillFormsFlowProps {
  patient: UserTiny;
  /** The forms that can be filled again; all start ticked. */
  forms: RefillFormTarget[];
  /**
   * The visit's required forms the patient has not filled yet. Staff can send
   * or fill them alongside; they have no copy to delete. All start ticked.
   */
  pendingForms?: RefillFormTarget["form"][];
  /** The appointment asked from; its forms step reopens and the send links to it. */
  eventId?: string;
  /** Every form the appointment requires, for the send's appointment link. */
  appointmentFormIds?: string[];
  /** The copies were archived; the caller refreshes what it lists. */
  onRefilled?: () => void;
  /** The flow is over: cancelled, filled in person, or the send window closed. */
  onClose: () => void;
}

/**
 * Asks a patient to fill forms they have already submitted again. Their current
 * copy is archived (as Archive Submission does), so the form shows as needed
 * until the new copy is in, and every link to it opens a fresh form instead of
 * "already submitted". Staff then fill it in person or send it; sending opens
 * Send Forms with those forms picked. The visit's still-needed forms can ride
 * along in the same fill or send. Mount it to start, unmount on `onClose`.
 */
export default function RefillFormsFlow({
  patient,
  forms,
  pendingForms,
  eventId,
  appointmentFormIds,
  onRefilled,
  onClose,
}: RefillFormsFlowProps) {
  const [sendFormIds, setSendFormIds] = useState<string[] | null>(null);

  if (sendFormIds) {
    return (
      <SendFormsDialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        patient={patient}
        appointmentId={eventId}
        initialFormIds={sendFormIds}
        appointmentFormIds={appointmentFormIds}
      />
    );
  }

  return (
    <RefillFormsDialog
      patient={patient}
      forms={forms}
      pendingForms={pendingForms}
      eventId={eventId}
      onClose={onClose}
      onReady={(how, formIds) => {
        onRefilled?.();
        if (how === "send") setSendFormIds(formIds);
        else onClose();
      }}
    />
  );
}

interface RefillFormsDialogProps {
  patient: Pick<UserTiny, "id" | "firstName">;
  forms: RefillFormTarget[];
  pendingForms?: RefillFormTarget["form"][];
  eventId?: string;
  onClose: () => void;
  /** The copies are archived; staff chose how the new ones get filled. */
  onReady: (how: "in_person" | "send", formIds: string[]) => void;
}

function RefillFormsDialog({
  patient,
  forms,
  pendingForms,
  eventId,
  onClose,
  onReady,
}: RefillFormsDialogProps) {
  const refillMutation = useRefillFormsMutation();
  const { openInPersonForm } = useInPersonForm();
  const [pendingAction, setPendingAction] = useState<
    "in_person" | "send" | null
  >(null);

  // Held from when the window opened: once the copies are archived the caller's
  // list empties, and the window must not change under staff while it closes.
  const [targets] = useState(forms);
  const [pending] = useState(() =>
    (pendingForms ?? []).filter(
      (form) => !forms.some((target) => target.form.id === form.id),
    ),
  );
  const [selectedIds, setSelectedIds] = useState(
    () =>
      new Set([
        ...forms.map((target) => target.form.id),
        ...pending.map((form) => form.id),
      ]),
  );
  const selected = targets.filter((target) => selectedIds.has(target.form.id));
  const selectedPending = pending.filter((form) => selectedIds.has(form.id));
  const refillFormIds = selected.map((target) => target.form.id);
  const fillForms = [
    ...selected.map((target) => target.form),
    ...selectedPending,
  ];
  const formIds = fillForms.map((form) => form.id);
  const single =
    targets.length === 1 && pending.length === 0 ? targets[0] : undefined;

  const toggle = (formId: string, checked: boolean) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (checked) next.add(formId);
      else next.delete(formId);
      return next;
    });
  };

  const refill = async () => {
    // Forms that were never submitted have no copy to delete.
    if (refillFormIds.length === 0) return;
    await refillMutation.mutateAsync({
      patientId: patient.id,
      formIds: refillFormIds,
      eventId,
    });
  };

  const handleFillInPerson = async () => {
    // Opened during the click: a tab opened after the request is refused by
    // popup blockers.
    const tab = window.open("", "_blank");
    setPendingAction("in_person");
    try {
      await refill();
    } catch {
      tab?.close();
      return;
    } finally {
      setPendingAction(null);
    }
    openInPersonForm(patient.id, fillForms, undefined, tab);
    onReady("in_person", formIds);
  };

  const handleSend = async () => {
    setPendingAction("send");
    try {
      await refill();
    } catch {
      return;
    } finally {
      setPendingAction(null);
    }
    toast({
      title:
        fillForms.length === 1 ? "Form ready to fill" : "Forms ready to fill",
      description: `Choose how to send ${fillForms.length === 1 ? "it" : "them"} to ${patient.firstName}.`,
      variant: "success",
    });
    onReady("send", formIds);
  };

  const isBusy = pendingAction !== null;
  const canSubmit = !isBusy && fillForms.length > 0;

  return (
    <Dialog open onOpenChange={(next) => !next && !isBusy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {single ? `Fill ${single.form.name} again?` : "Fill forms again"}
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4 text-body text-foreground-secondary">
            {/* Only "Fill again" picks lose a submission; still-needed forms have none. */}
            {selected.length > 0 && (
              <Alert variant="warning">
                <AlertDescription>
                  {single
                    ? `${patient.firstName}'s previous submission${
                        single.completedDate
                          ? ` from ${formatDate(single.completedDate)}`
                          : ""
                      } will be deleted. The form will show as needed until they fill it again.`
                    : selected.length === 1
                      ? `${patient.firstName}'s previous submission of ${selected[0].form.name} will be deleted. It will show as needed until they fill it again.`
                      : `${patient.firstName}'s previous submissions of the ${selected.length} forms ticked under Fill again will be deleted. They will show as needed until they fill them again.`}
                </AlertDescription>
              </Alert>
            )}

            {!single && (
              <div className="space-y-2">
                {pending.length > 0 && (
                  <p className="text-caption font-medium text-foreground-secondary">
                    Fill again
                  </p>
                )}
                {targets.map((target) => (
                  <label
                    key={target.form.id}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <Checkbox
                      checked={selectedIds.has(target.form.id)}
                      onCheckedChange={(checked) =>
                        toggle(target.form.id, checked === true)
                      }
                      disabled={isBusy}
                    />
                    <span className="min-w-0 flex-1 text-foreground">
                      {target.form.name}
                    </span>
                    {target.completedDate && (
                      <span className="shrink-0 text-caption text-foreground-muted">
                        Submitted {formatDate(target.completedDate)}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}

            {pending.length > 0 && (
              <div className="space-y-2">
                <div>
                  <p className="text-caption font-medium text-foreground-secondary">
                    Still needed for this visit
                  </p>
                  <p className="text-caption text-foreground-muted">
                    Required forms {patient.firstName} hasn&apos;t filled yet.
                    Nothing is deleted for these.
                  </p>
                </div>
                {pending.map((form) => (
                  <label
                    key={form.id}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <Checkbox
                      checked={selectedIds.has(form.id)}
                      onCheckedChange={(checked) =>
                        toggle(form.id, checked === true)
                      }
                      disabled={isBusy}
                    />
                    <span className="min-w-0 flex-1 text-foreground">
                      {form.name}
                    </span>
                    <span className="shrink-0 text-caption text-foreground-muted">
                      Not filled
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            icon={<LuSend className="h-4 w-4" />}
            onClick={handleSend}
            isLoading={pendingAction === "send"}
            disabled={!canSubmit}
          >
            Send to patient
          </Button>
          <Button
            variant="primary"
            icon={<LuClipboardList className="h-4 w-4" />}
            onClick={handleFillInPerson}
            isLoading={pendingAction === "in_person"}
            disabled={!canSubmit}
          >
            Fill in person
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
